import React, { useEffect, useRef, useState } from "react"
import PropTypes from "prop-types"
import { Link, useNavigate, useParams } from "react-router-dom"
import { Alert, Spinner } from "reactstrap"
import Breadcrumbs from "components/Common/Breadcrumb2"
import CampaignPermissionNotice from "components/Common/CampaignPermissionNotice"
import useCurrentPermissions from "hooks/useCurrentPermissions"
import { get, post, put } from "helpers/api_helper"
import { getApiErrorMessage } from "helpers/api_error_helper"
import { channelLabel, couponApi, couponPayload, defaultMessage, inputDate } from "./couponHelpers"

const emptyCoupon = {
  name: "", type: "coupon", entry_channel: "line_man", line_campaign_id: "", description: "",
  message_template: defaultMessage, starts_at: "", expires_at: "", active: true,
}

export default function CouponForm({ mode = "create" }) {
  const { id } = useParams()
  const navigate = useNavigate()
  const permissions = useCurrentPermissions("Unable to verify coupon permissions.")
  const canSave = permissions.hasScope(`coupon:${mode === "create" ? "create" : "update"}`)
  const [values, setValues] = useState(emptyCoupon)
  const [campaigns, setCampaigns] = useState([])
  const mappingLocked = mode !== "create"
  const [loading, setLoading] = useState(true)
  const formKey = mode === "create" ? "create" : String(id)
  const [loadedKey, setLoadedKey] = useState(null)
  const [loadError, setLoadError] = useState("")
  const [saveError, setSaveError] = useState("")
  const [saving, setSaving] = useState(false)
  const saveLock = useRef(false)
  const campaignHasChannel = (campaign, channel) => (campaign?.coupons || []).some(coupon => coupon.entry_channel === channel && String(coupon.id) !== String(id))
  const update = event => {
    const { name, value, checked, type } = event.target
    setValues(current => {
      const next = { ...current, [name]: type === "checkbox" ? checked : value }
      if (name === "entry_channel" && campaignHasChannel(campaigns.find(campaign => String(campaign.id) === String(current.line_campaign_id)), value)) next.line_campaign_id = ""
      return next
    })
  }

  useEffect(() => {
    let active = true
    setLoading(true)
    setLoadedKey(null)
    setLoadError("")
    const load = async () => {
      try {
        const [options, detail] = await Promise.all([
          get(`${couponApi()}/options`),
          mode === "create" ? Promise.resolve(null) : get(`${couponApi()}/${id}`),
        ])
        if (!Array.isArray(options?.data?.campaigns) || (mode !== "create" && !detail?.data?.id)) throw new Error("Invalid coupon response")
        if (!active) return
        setCampaigns(options.data.campaigns)
        if (detail) {
          const data = detail.data
          setValues({ ...emptyCoupon, ...data, description: data.description || "", starts_at: inputDate(data.starts_at), expires_at: inputDate(data.expires_at) })
        } else setValues(emptyCoupon)
        setLoadedKey(mode === "create" ? "create" : String(id))
      } catch (failure) {
        if (active) setLoadError(getApiErrorMessage(failure, "Unable to load coupon settings."))
      } finally { if (active) setLoading(false) }
    }
    load()
    return () => { active = false }
  }, [id, mode])

  const save = async event => {
    event.preventDefault()
    if (!canSave || loading || loadedKey !== formKey || loadError || saveLock.current) return
    setSaveError("")
    if (campaignHasChannel(campaigns.find(campaign => String(campaign.id) === String(values.line_campaign_id)), values.entry_channel)) {
      setSaveError(`This LINE campaign already has a ${channelLabel(values.entry_channel)} configuration.`)
      return
    }
    let payload
    try { payload = couponPayload(values) }
    catch (failure) { setSaveError(failure.message); return }
    saveLock.current = true
    setSaving(true)
    try {
      const result = mode === "create" ? await post(couponApi(), payload) : await put(`${couponApi()}/${id}`, payload)
      if (!result?.data?.id) throw new Error("Invalid save response")
      navigate(`/coupon/view/${result.data.id}`)
    } catch (failure) {
      setSaveError(getApiErrorMessage(failure, "Unable to save coupon. Your entries have been kept."))
    } finally {
      saveLock.current = false
      setSaving(false)
    }
  }

  const selectedCampaign = campaigns.find(campaign => String(campaign.id) === String(values.line_campaign_id))
  const denied = !permissions.loading && !permissions.error && !canSave
  const preview = values.message_template.replaceAll("{name}", values.name || "Reward name").replaceAll("{code}", "EXAMPLE-CODE").replaceAll("{expires_at}", values.expires_at?.replace("T", " ") || "No expiry")

  return (
    <div className="page-content"><div className="container-fluid">
      <Breadcrumbs title={mode === "create" ? "Create coupon / voucher" : "Edit coupon / voucher"} breadcrumbItems={[{ title: "Coupons & Vouchers", link: "coupon" }, { title: mode === "create" ? "Create" : "Edit", link: mode === "create" ? "create" : `edit/${id}` }]} />
      <div className="bInnerContent">
        <CampaignPermissionNotice permissions={permissions} permissionLabel="coupon" deniedMessage={denied ? "Your account does not have permission to save this coupon." : ""} />
        {loadError && <Alert color="danger">{loadError}</Alert>}
        {loading || (!loadError && loadedKey !== formKey) ? <Spinner color="primary" aria-label="Loading coupon settings" /> : !loadError && <form onSubmit={save}>
          <Alert color="info">
            <strong>เงื่อนไขการรับรางวัล</strong><br />
            ผู้ตอบรับรางวัลได้หนึ่งครั้งต่อ LINE UID ต่อแบบสอบถาม รวมทุกช่องทาง และได้รับรางวัลเมื่อผ่านแบบสอบถามเท่านั้น
            <div className="mt-2">A LINE campaign can have one LINE MAN configuration and one Grab configuration. Create a separate coupon or voucher configuration for each channel in the same campaign.</div>
          </Alert>
          {mappingLocked && <Alert color="info">The entry channel and LINE campaign are fixed after creation. Create another reward configuration to use a different campaign or channel.</Alert>}
          <fieldset disabled={!canSave || saving}>
            <div className="row">
              <div className="col-md-8 mb-3"><label className="form-label" htmlFor="coupon-name">Name *</label><input id="coupon-name" className="form-control" name="name" value={values.name} onChange={update} maxLength={255} required /></div>
              <div className="col-md-4 mb-3"><label className="form-label" htmlFor="coupon-type">Reward type *</label><select id="coupon-type" className="form-select" name="type" value={values.type} onChange={update}><option value="coupon">Coupon</option><option value="voucher">Voucher</option></select></div>
              <div className="col-md-4 mb-3"><label className="form-label" htmlFor="coupon-channel">Entry channel *</label><select id="coupon-channel" className="form-select" name="entry_channel" value={values.entry_channel} onChange={update} disabled={mappingLocked}><option value="line_man">LINE MAN</option><option value="grab">Grab</option></select></div>
              <div className="col-md-8 mb-3"><label className="form-label" htmlFor="coupon-campaign">LINE campaign *</label><select id="coupon-campaign" className="form-select" name="line_campaign_id" value={values.line_campaign_id} onChange={update} disabled={mappingLocked} required>
                <option value="">Select a LINE campaign</option>
                {campaigns.map(campaign => <option key={campaign.id} value={campaign.id} disabled={campaignHasChannel(campaign, values.entry_channel)}>{campaign.campaign_name || `Campaign #${campaign.id}`} · Survey #{campaign.survey_id}{campaignHasChannel(campaign, values.entry_channel) ? ` (${channelLabel(values.entry_channel)} already configured)` : ""}</option>)}
              </select>
                {selectedCampaign && <small className="text-muted">LINE OA: {selectedCampaign.oa_name || `#${selectedCampaign.line_oa_id}`} · Survey #{selectedCampaign.survey_id} · {channelLabel(values.entry_channel)}</small>}
                {!campaigns.length && <small className="text-danger">No LINE campaigns are available. Create a LINE campaign first.</small>}
              </div>
              <div className="col-md-6 mb-3"><label className="form-label" htmlFor="coupon-start">Starts at (optional)</label><input id="coupon-start" className="form-control" name="starts_at" type="datetime-local" value={values.starts_at} onChange={update} /></div>
              <div className="col-md-6 mb-3"><label className="form-label" htmlFor="coupon-expiry">Expires at (optional)</label><input id="coupon-expiry" className="form-control" name="expires_at" type="datetime-local" value={values.expires_at} onChange={update} /><small className="text-muted">Dates use your browser&apos;s local time zone.</small></div>
              <div className="col-12 mb-3"><label className="form-label" htmlFor="coupon-description">Description</label><textarea id="coupon-description" className="form-control" name="description" value={values.description} onChange={update} maxLength={4000} rows={2} /></div>
              <div className="col-md-6 mb-3"><label className="form-label" htmlFor="coupon-message">LINE text message *</label><textarea id="coupon-message" className="form-control" name="message_template" value={values.message_template} onChange={update} rows={5} maxLength={4000} required /><small className="text-muted">Available placeholders: {"{name}"}, {"{code}"}, {"{expires_at}"}. Include {"{code}"} to deliver the reward.</small></div>
              <div className="col-md-6 mb-3"><p className="form-label">Message preview</p><div className="border rounded p-3 bg-light" style={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>{preview}</div><small className="text-muted">Example code only. The assigned code is inserted when sent.</small></div>
              <div className="col-12 mb-3"><div className="form-check form-switch"><input id="coupon-active" className="form-check-input" name="active" type="checkbox" checked={values.active} onChange={update} /><label className="form-check-label" htmlFor="coupon-active">Active — allow automatic rewards</label></div><small className="text-muted">Import codes after saving. If stock runs out, eligible deliveries wait for new codes. Turning this off stops new allocations and allocations awaiting stock. Already allocated rewards will still be delivered.</small></div>
            </div>
          </fieldset>
          {saveError && <Alert color="danger">{saveError}</Alert>}
          <div className="d-flex gap-2 justify-content-end">
            <Link className={`btn btn-outline-secondary${saving ? " disabled" : ""}`} to={mode === "create" ? "/coupon" : `/coupon/view/${id}`}>Cancel</Link>
            <button type="submit" className="btn btn-primary" disabled={!canSave || saving || !campaigns.length}>{saving ? "Saving..." : "Save coupon"}</button>
          </div>
        </form>}
        {loadError && <Link className="btn btn-outline-secondary" to="/coupon">Back to coupons</Link>}
      </div>
    </div></div>
  )
}

CouponForm.propTypes = { mode: PropTypes.oneOf(["create", "update"]) }
